import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CREATE_SENDOUT,
  GET,
  GET_SENDOUT_BY_ID,
  GET_SENDOUT_CLIENTS,
  GET_SENDOUT_DETAILS,
  GET_SENDOUT_FILTER_DATA,
  GET_SENDOUT_KEYWORDS,
  GET_SENDOUTS,
  POST,
  PREPARE_SENDOUT_CREATION,
  PREVIEW_SENDOUT_MESSAGE,
  PUT,
  SENDOUTS_RESOURCE,
  START_SENDOUT,
  STOP_SENDOUT,
} from '@app/constants';
import type { IBaseQueryParams } from '@app/services/rtkQuery/baseQuery';
import { customBaseQuery } from '@app/services/rtkQuery/baseQuery';
import type { NSendoutsApi } from '@app/services/rtkQuery/Sendouts/types';
import { arrayToQueryString } from '@app/utils/arrayToQueryString';

type URLParams =
  | string
  | Record<string, string>
  | string[][]
  | URLSearchParams
  | undefined;

const buildGetSendoutClientsQueryString = (
  query: NSendoutsApi.GetSendoutClientsRequest
): string => {
  const searchParams = new URLSearchParams();

  const append = (key: string, value: unknown) => {
    if (value === undefined || value === null) return;
    searchParams.append(key, String(value));
  };

  append('SendoutId', query.SendoutId);
  append('SearchTerm', query.SearchTerm);

  if (query.StatusIds?.length) {
    for (const status of query.StatusIds) append('StatusIds', status);
  }

  if (query.ActionIds?.length) {
    for (const action of query.ActionIds) append('ActionIds', action);
  }

  append('ItemsPerPage', query.ItemsPerPage);
  append('PageN', query.PageN);
  append('ActiveBranchId', query.ActiveBranchId);

  return searchParams.toString();
};

export const sendoutsApi = createApi({
  reducerPath: 'sendoutsApi',
  baseQuery: customBaseQuery,
  tagTypes: ['SendoutClients'] as const,
  endpoints: ({ query, mutation }) => ({
    [GET_SENDOUT_BY_ID]: query<
      NSendoutsApi.GetSendoutByIdResponse,
      NSendoutsApi.GetSendoutByIdRequest
    >({
      query: ({ ...params }) => ({
        url: `${SENDOUTS_RESOURCE}/${GET_SENDOUT_BY_ID}/`,
        method: GET,
        params,
      }),
    }),
    [GET_SENDOUTS]: query<
      NSendoutsApi.GetSendoutsResponse,
      NSendoutsApi.GetSendoutsRequest
    >({
      query: ({ StatusIds, ChannelIds, IntegrationIds, ...params }) => {
        const statusQuery = arrayToQueryString('StatusIds', StatusIds);
        const channelQuery = arrayToQueryString('ChannelIds', ChannelIds);
        const integrationQuery = arrayToQueryString(
          'IntegrationIds',
          IntegrationIds
        );
        const queryString = [statusQuery, channelQuery, integrationQuery]
          .filter(Boolean)
          .join('&');

        return {
          url: `${SENDOUTS_RESOURCE}/${GET_SENDOUTS}/`,
          method: GET,
          params,
          paramsSerializer: (params: URLParams) => {
            const serializedParams = new URLSearchParams(params).toString();
            return `${serializedParams}&${queryString}`;
          },
        };
      },
    }),
    // На 12.03.2025 этот метод возвращал только активные интеграции
    [PREPARE_SENDOUT_CREATION]: query<
      NSendoutsApi.PrepareSendoutCreationResponse,
      NSendoutsApi.PrepareSendoutCreationRequest
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();

        for (const [key, value] of Object.entries(params)) {
          if (value === undefined || value === null) {
            continue;
          }

          if (Array.isArray(value)) {
            value.forEach((item) => queryParams.append(key, item.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }

        return {
          url: `${SENDOUTS_RESOURCE}/${PREPARE_SENDOUT_CREATION}`,
          method: GET,
          params: queryParams,
        };
      },
    }),
    [CREATE_SENDOUT]: mutation<
      NSendoutsApi.CreateSendoutResponse,
      NSendoutsApi.CreateSendoutRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: `${SENDOUTS_RESOURCE}/${CREATE_SENDOUT}/`,
        method: POST,
        params: { ActiveBranchId },
        data,
      }),
    }),
    [START_SENDOUT]: mutation<
      NSendoutsApi.StartSendoutResponse,
      NSendoutsApi.StartSendoutRequest
    >({
      query: ({ ...params }) => ({
        url: `${SENDOUTS_RESOURCE}/${START_SENDOUT}/`,
        method: POST,
        params,
      }),
    }),
    [STOP_SENDOUT]: mutation<
      NSendoutsApi.StopSendoutResponse,
      NSendoutsApi.StopSendoutRequest
    >({
      query: ({ ...params }) => ({
        url: `${SENDOUTS_RESOURCE}/${STOP_SENDOUT}/`,
        method: POST,
        params,
      }),
    }),
    [GET_SENDOUT_FILTER_DATA]: query<
      NSendoutsApi.GetSendoutFilterDataResponse,
      {
        params: NSendoutsApi.GetSendoutFilterDataRequest;
        logs?: IBaseQueryParams['logs'];
      }
    >({
      query: ({ params, logs }) => ({
        url: `${SENDOUTS_RESOURCE}/${GET_SENDOUT_FILTER_DATA}/`,
        method: GET,
        params,
        logs,
      }),
    }),
    [PREVIEW_SENDOUT_MESSAGE]: query<
      string[],
      NSendoutsApi.PreviewSendoutMessagesRequest
    >({
      query: ({ ...data }) => ({
        url: `${SENDOUTS_RESOURCE}/${PREVIEW_SENDOUT_MESSAGE}/`,
        method: POST,
        data,
      }),
    }),
    [GET_SENDOUT_DETAILS]: query<
      NSendoutsApi.GetSendoutDetailsResponse,
      NSendoutsApi.GetSendoutDetailsRequest
    >({
      query: ({ ...params }) => ({
        url: `${SENDOUTS_RESOURCE}/${GET_SENDOUT_DETAILS}/`,
        method: GET,
        params,
      }),
    }),
    [GET_SENDOUT_CLIENTS]: query<
      NSendoutsApi.GetSendoutClientsResponse,
      NSendoutsApi.GetSendoutClientsRequest
    >({
      query: (queryArgs) => {
        const queryString = buildGetSendoutClientsQueryString(queryArgs);

        return {
          url: `${SENDOUTS_RESOURCE}/${GET_SENDOUT_CLIENTS}${
            queryString ? `?${queryString}` : ''
          }`,
          method: GET,
        };
      },
      providesTags: (_result, _error, arg) => [
        { type: 'SendoutClients', id: arg.SendoutId ?? 'UNKNOWN' },
      ],
    }),
    [GET_SENDOUT_KEYWORDS]: query<
      NSendoutsApi.GetSendoutKeywordsResponse,
      NSendoutsApi.GetSendoutKeywordsRequest
    >({
      query: (params) => ({
        url: `${SENDOUTS_RESOURCE}/${GET_SENDOUT_KEYWORDS}`,
        method: GET,
        params,
      }),
    }),
    GetSendoutForEdit: query<
      NSendoutsApi.ModelEditSendout,
      NSendoutsApi.GetSendoutForEditRequest
    >({
      query: (params) => ({
        url: `${SENDOUTS_RESOURCE}/GetSendoutForEdit`,
        method: GET,
        params,
      }),
    }),
    EditSendout: mutation<
      NSendoutsApi.Sendout,
      NSendoutsApi.EditSendoutRequest
    >({
      query: ({ ActiveBranchId, data }) => ({
        url: `${SENDOUTS_RESOURCE}/EditSendout`,
        method: PUT,
        params: { ActiveBranchId },
        data,
      }),
    }),
  }),
});

export const {
  useGetSendoutByIdQuery,
  useGetSendoutsQuery,
  usePrepareSendoutCreationQuery,
  useCreateSendoutMutation,
  useStartSendoutMutation,
  useStopSendoutMutation,
  useGetSendoutFilterDataQuery,
  useLazyPrepareSendoutCreationQuery,
  useLazyPreviewSendoutMessagesQuery,
  useGetSendoutDetailsQuery,
  useGetSendoutClientsQuery,
  useGetSendoutKeywordsQuery,
  useGetSendoutForEditQuery,
  useLazyGetSendoutForEditQuery,
  useEditSendoutMutation,
} = sendoutsApi;
